/**
 * Select rows of a list and act on all of them (the domain list's bulk bar, for other lists):
 * one form and one confirmation, then each row is sent on its own through the same call as its
 * page (no bulk endpoint), one after another, with its result; failed rows can be retried (same
 * idempotency key unless the server refused the first try). "Xuất CSV" downloads the selected
 * rows as shown.
 */

import { CheckCircle2, CircleDashed, Download, Loader2, X, XCircle, type LucideIcon } from 'lucide-react';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { isApiError } from '../api';
import { newIdempotencyKey } from '../lib/ids';
import { Callout, Field, type Column } from './blocks';
import { errorText } from '../lib/errorText';

export interface BulkInput {
  choice: string;
  reason: string;
}

export interface BulkAction<T> {
  id: string;
  label: string;
  icon: LucideIcon;
  /** A value to pick for all rows (a status, a tier, a person). */
  choice?: { label: string; options: readonly { value: string; label: string }[] };
  /** A reason is asked (kept in the audit log). */
  reason: boolean;
  /** Why this row is left out (never sent), or null. */
  skip?: (row: T, input: BulkInput) => string | null;
  /** Sends one row; returns the note shown on its line. */
  run: (row: T, input: BulkInput, idempotencyKey: string) => Promise<string>;
}

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

/** The selection of a paged list: kept across pages, cleared on demand. */
export function useSelection<T>(rowKey: (row: T) => string) {
  const [selected, setSelected] = useState<Map<string, T>>(new Map());
  const set = (rows: readonly T[], on: boolean) =>
    setSelected((m) => {
      const next = new Map(m);
      for (const r of rows) {
        if (on) next.set(rowKey(r), r);
        else next.delete(rowKey(r));
      }
      return next;
    });
  return {
    rows: [...selected.values()],
    count: selected.size,
    clear: () => setSelected(new Map()),
    /** The checkbox column; clicks on it never open the row. */
    column: (pageRows: readonly T[], label: (row: T) => string, allLabel: string): Column<T> => ({
      key: 'select',
      header: (
        <Checkbox
          checked={pageRows.length > 0 && pageRows.every((r) => selected.has(rowKey(r)))}
          onCheckedChange={(v) => set(pageRows, v === true)}
          aria-label={allLabel}
        />
      ),
      className: 'w-8 pr-0',
      cell: (r) => (
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- only stops the row's own click; the checkbox is the control
        <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected.has(rowKey(r))} onCheckedChange={(v) => set([r], v === true)} aria-label={label(r)} />
        </span>
      ),
    }),
  };
}

/** A cell as a spreadsheet reads it: quoted, and never taken for a formula. */
function csvCell(v: string | number | null | undefined): string {
  const s = v === null || v === undefined ? '' : String(v);
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r;]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function downloadCsv<T>(name: string, columns: readonly CsvColumn<T>[], rows: readonly T[]) {
  const lines = [columns.map((c) => csvCell(c.header)).join(','), ...rows.map((r) => columns.map((c) => csvCell(c.value(r))).join(','))];
  // BOM: spreadsheet programs then read the Vietnamese text as UTF-8.
  const blob = new Blob(['﻿', lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

/** The bar above the list while rows are selected, and the dialog of the action picked. */
export function BulkList<T>({
  selection,
  actions,
  csv,
  rowKey,
  rowLabel,
  onDone,
}: {
  selection: ReturnType<typeof useSelection<T>>;
  actions: readonly BulkAction<T>[];
  csv: { name: string; columns: readonly CsvColumn<T>[] };
  rowKey: (row: T) => string;
  rowLabel: (row: T) => ReactNode;
  /** After a run in which something changed. */
  onDone: () => void;
}) {
  const { t } = useTranslation('ux');
  const [action, setAction] = useState<BulkAction<T> | null>(null);
  if (!selection.count && !action) return null;
  return (
    <>
      {selection.count ? (
        <div className="sticky top-2 z-10 mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-card px-3 py-2 shadow-sm" role="region" aria-label={t('bulkList.selected', { count: selection.count })} data-bulk-bar>
          <span className="mr-1 text-sm font-medium">{t('bulkList.selected', { count: selection.count })}</span>
          {actions.map((a, i) => (
            <Button key={a.id} type="button" size="sm" variant={i === 0 ? 'default' : 'outline'} onClick={() => setAction(a)}>
              <a.icon /> {a.label}
            </Button>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={() => downloadCsv(csv.name, csv.columns, selection.rows)}>
            <Download /> {t('bulkList.export')}
          </Button>
          <Button type="button" size="sm" variant="ghost" className="ml-auto" onClick={selection.clear}>
            <X /> {t('bulk.clear')}
          </Button>
        </div>
      ) : null}
      {action ? (
        <RunDialog
          action={action}
          rows={selection.rows}
          rowKey={rowKey}
          rowLabel={rowLabel}
          onClose={(changed) => {
            setAction(null);
            if (changed) {
              selection.clear();
              onDone();
            }
          }}
        />
      ) : null}
    </>
  );
}

type RowState = { phase: 'waiting' } | { phase: 'running' } | { phase: 'ok'; note: string } | { phase: 'failed'; note: string } | { phase: 'skipped'; note: string };

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

function RunDialog<T>({
  action,
  rows,
  rowKey,
  rowLabel,
  onClose,
}: {
  action: BulkAction<T>;
  rows: readonly T[];
  rowKey: (row: T) => string;
  rowLabel: (row: T) => ReactNode;
  onClose: (changed: boolean) => void;
}) {
  const { t } = useTranslation('ux');
  const [choice, setChoice] = useState(action.choice?.options[0]?.value ?? '');
  const [reason, setReason] = useState('');
  const [state, setState] = useState<Record<string, RowState>>({});
  const [running, setRunning] = useState(false);
  const keys = useRef<Record<string, string>>({});
  const started = Object.keys(state).length > 0;
  const input: BulkInput = { choice, reason: reason.trim() };

  const one = async (r: T): Promise<RowState> => {
    const skipped = action.skip?.(r, input);
    if (skipped) return { phase: 'skipped', note: skipped };
    const k = (keys.current[rowKey(r)] ??= newIdempotencyKey());
    try {
      return { phase: 'ok', note: await action.run(r, input, k) };
    } catch (e) {
      // Refused (4xx): nothing was applied, so a retry is a new request. No answer or a server
      // error: the retry keeps the key, so it cannot apply twice.
      if (isApiError(e) && e.status >= 400 && e.status < 500) delete keys.current[rowKey(r)];
      return { phase: 'failed', note: errorText(e).text };
    }
  };
  const run = async (targets: readonly T[]) => {
    setRunning(true);
    setState((s) => ({ ...s, ...Object.fromEntries(targets.map((r) => [rowKey(r), { phase: 'waiting' } as RowState])) }));
    for (const r of targets) {
      setState((s) => ({ ...s, [rowKey(r)]: { phase: 'running' } }));
      const result = await one(r);
      setState((s) => ({ ...s, [rowKey(r)]: result }));
    }
    setRunning(false);
  };
  const close = () => {
    if (!running) onClose(Object.values(state).some((s) => s.phase === 'ok'));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(rows);
  };

  const values = Object.values(state);
  const done = values.filter((s) => s.phase !== 'waiting' && s.phase !== 'running').length;
  const ok = values.filter((s) => s.phase === 'ok').length;
  const failed = rows.filter((r) => state[rowKey(r)]?.phase === 'failed');
  const valid = (!action.reason || input.reason.length > 0) && (!action.choice || Boolean(choice));

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('bulkList.title', { action: action.label, count: rows.length })}</DialogTitle>
          <DialogDescription>{t('bulkList.description')}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={submit}>
          {!started && action.choice ? (
            <Field label={action.choice.label}>
              <select className={selectClass} value={choice} onChange={(e) => setChoice(e.target.value)}>
                {action.choice.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          {!started && action.reason ? (
            <Field label={t('bulk.reason')}>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required />
            </Field>
          ) : null}
          {started ? (
            <div className="grid gap-2" aria-live="polite">
              <Progress value={(done / Math.max(1, rows.length)) * 100} />
              <p className="text-sm">{running ? t('bulk.running', { done, total: rows.length }) : t('bulk.finished', { ok, failed: failed.length })}</p>
            </div>
          ) : null}
          <ul className="max-h-72 divide-y overflow-y-auto rounded-md border text-sm">
            {rows.map((r) => {
              const s = state[rowKey(r)];
              return (
                <li key={rowKey(r)} className="flex items-start gap-2 px-3 py-2" data-bulk-row={s?.phase ?? 'idle'}>
                  <RowIcon state={s} />
                  <span className="min-w-0 font-medium break-words">{rowLabel(r)}</span>
                  <span className="ml-auto min-w-0 text-right text-xs text-muted-foreground break-words">{s ? ('note' in s && s.note ? s.note : t(`bulk.status.${s.phase}`)) : ''}</span>
                </li>
              );
            })}
          </ul>
          {!running && failed.length && started ? <Callout tone="warn">{t('bulk.finished', { ok, failed: failed.length })}</Callout> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={running}>
              {t('bulk.close')}
            </Button>
            {!started ? (
              <Button type="submit" disabled={!valid}>
                {t('bulkList.confirm', { count: rows.length })}
              </Button>
            ) : failed.length && !running ? (
              <Button type="button" onClick={() => void run(failed)}>
                {t('bulk.retry', { count: failed.length })}
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RowIcon({ state }: { state?: RowState }) {
  const c = 'mt-0.5 size-4 shrink-0';
  if (!state || state.phase === 'waiting') return <CircleDashed className={`${c} text-muted-foreground`} aria-hidden="true" />;
  if (state.phase === 'running') return <Loader2 className={`${c} animate-spin text-primary`} aria-hidden="true" />;
  if (state.phase === 'ok') return <CheckCircle2 className={`${c} text-success`} aria-hidden="true" />;
  if (state.phase === 'failed') return <XCircle className={`${c} text-critical`} aria-hidden="true" />;
  return <CircleDashed className={`${c} text-warning`} aria-hidden="true" />;
}
